// Offre en magasin : informations déduites du texte lu sur la photo (OCR) d'une étiquette ou d'une affiche promo.
// Fonctions pures (testées dans in-store-parser.spec.ts) ; le membre relit et complète toujours le formulaire.

export interface InStoreTextDetection {
  title: string;
  storeName: string;
  priceDiscount: number | null;
  price: number | null;
  percent: number | null;
  // AAAA-MM-JJ, '' si non trouvée
  startDate: string;
  expirationDate: string;
}

// Enseignes reconnues sur la photo : [nom affiché, variantes sans accent ni majuscule]
export const KNOWN_STORES: [string, string[]][] = [
  ['Lidl', ['lidl']],
  ['Aldi', ['aldi']],
  ['Carrefour', ['carrefour']],
  ['E.Leclerc', ['leclerc', 'e.leclerc']],
  ['Auchan', ['auchan']],
  ['Intermarché', ['intermarche']],
  ['Super U', ['super u', 'hyper u', 'systeme u', 'u express']],
  ['Monoprix', ['monoprix']],
  ['Casino', ['casino', 'geant casino']],
  ['Franprix', ['franprix']],
  ['Netto', ['netto']],
  ['Cora', ['cora']],
  ['Match', ['supermarche match']],
  ['Grand Frais', ['grand frais']],
  ['Picard', ['picard']],
  ['Biocoop', ['biocoop']],
  ['Action', ['action']],
  ['Gifi', ['gifi']],
  ['La Foir\'Fouille', ['foirfouille', 'foir\'fouille']],
  ['Decathlon', ['decathlon']],
  ['Intersport', ['intersport']],
  ['Fnac', ['fnac']],
  ['Darty', ['darty']],
  ['Boulanger', ['boulanger']],
  ['Leroy Merlin', ['leroy merlin']],
  ['Castorama', ['castorama']],
  ['Brico Dépôt', ['brico depot']],
  ['Bricomarché', ['bricomarche']],
  ['IKEA', ['ikea']],
  ['Conforama', ['conforama']],
  ['But', ['but']],
  ['Kiabi', ['kiabi']],
  ['Primark', ['primark']],
  ['H&M', ['h&m']],
  ['Zara', ['zara']],
  ['Sephora', ['sephora']],
  ['Nocibé', ['nocibe']],
  ['Norauto', ['norauto']],
  ['Feu Vert', ['feu vert']],
  ['Jardiland', ['jardiland']],
  ['Truffaut', ['truffaut']],
  ['Maxi Zoo', ['maxi zoo']],
  ['Cultura', ['cultura']],
  ['King Jouet', ['king jouet']],
  ['JouéClub', ['joueclub']],
];

const MONTHS = ['janv', 'fevr', 'mars', 'avr', 'mai', 'juin', 'juil', 'aout', 'sept', 'oct', 'nov', 'dec'];
// Lignes de prix à l'unité (le kg, le litre…) : jamais le prix du deal
const UNIT_PRICE_LINE = /(\bkg\b|\bkilo|\blitre|\ble l\b|\/\s?l\b|\/\s?kg|\bprix au\b|\bsoit\b|\bles? 100\s?g)/;
// Montant d'une remise (« économisez 2,00 € », « remise immédiate 1 € »)
const SAVINGS_LINE = /(econom|remise|reduction|cagnott|rembours|offert)/;
const NOT_TITLE = /(€|%|\bprix\b|\boffre\b|\bpromo|valable|\bdu\b.*\bau\b|au lieu|remise|econom|\bsoit\b|www|http|\blot\b|\bkg\b|carte|fidelite)/;
const PRICE_PATTERN = new RegExp([
  // 1€99, 1 € 99
  '(\\d{1,4})\\s?€\\s?(\\d{2})(?!\\d)',
  // 1,99 € / 1.99 / € 1,99 (pas une date 03.11.2026, pas une quantité 0,75 l)
  '(?<![\\d.,/])(\\d{1,4})\\s?[,.]\\s?(\\d{2})(?![\\d.,/])(?!\\s?(?:k?g|c?l|ml|m|cm|mm|kw|w|v|x)\\b)',
  // 5 €, 5€
  '(?<![\\d.,])(\\d{1,4})\\s?€(?!\\s?\\d)',
].join('|'), 'g');

// Texte sans accents, en minuscules (« Économisez » -> « economisez »)
export function normalizeText(text: string): string {
  return (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Prix trouvés dans une ligne, dans l'ordre
export function pricesIn(line: string): number[] {
  const prices: number[] = [];
  for (const match of line.matchAll(PRICE_PATTERN)) {
    const [, euros1, cents1, euros2, cents2, euros3] = match;
    const value = euros1 !== undefined ? Number(euros1) + Number(cents1) / 100
      : euros2 !== undefined ? Number(euros2) + Number(cents2) / 100
        : Number(euros3);
    if (value > 0 && value < 100000) {
      prices.push(round2(value));
    }
  }
  return prices;
}

export function detectStore(text: string): string {
  const normalized = ' ' + normalizeText(text).replace(/[^a-z0-9&'.]+/g, ' ') + ' ';
  for (const [name, variants] of KNOWN_STORES) {
    if (variants.some(variant => normalized.includes(' ' + variant + ' '))) {
      return name;
    }
  }
  return '';
}

function isoDate(year: number, month: number, day: number): string {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}

// Année absente : celle d'aujourd'hui, ou la suivante si la date serait passée depuis plus de 6 mois (fin décembre -> janvier)
function withYear(day: number, month: number, year: number | null, today: Date): string {
  if (year !== null) {
    return isoDate(year < 100 ? 2000 + year : year, month, day);
  }
  const current = today.getFullYear();
  const date = isoDate(current, month, day);
  if (!date) {
    return '';
  }
  const sixMonthsAgo = new Date(today.getTime() - 183 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return date < sixMonthsAgo ? isoDate(current + 1, month, day) : date;
}

// Dates du texte (JJ/MM, JJ/MM/AAAA, JJ-MM-AA, JJ.MM.AAAA, « 9 novembre »), dans l'ordre
export function datesIn(text: string, today: Date): string[] {
  const normalized = normalizeText(text);
  const found: { index: number; date: string }[] = [];
  const numeric = /(?<![\d,.])(\d{1,2})\s?(?:([/-])\s?(\d{1,2})(?:\2(\d{2,4}))?|\.(\d{1,2})\.(\d{2,4}))(?![\d,])/g;
  for (const match of normalized.matchAll(numeric)) {
    const day = Number(match[1]);
    const month = Number(match[3] ?? match[5]);
    const yearText = match[4] ?? match[6];
    const date = month >= 1 && month <= 12 && day >= 1 && day <= 31 ? withYear(day, month, yearText ? Number(yearText) : null, today) : '';
    if (date) {
      found.push({ index: match.index, date });
    }
  }
  const named = new RegExp(`(?<!\\d)(\\d{1,2})(?:er)?\\s+(${MONTHS.join('|')})[a-z]*\\.?(?:\\s+(\\d{4}))?`, 'g');
  for (const match of normalized.matchAll(named)) {
    const date = withYear(Number(match[1]), MONTHS.indexOf(match[2]) + 1, match[3] ? Number(match[3]) : null, today);
    if (date) {
      found.push({ index: match.index, date });
    }
  }
  return found.sort((a, b) => a.index - b.index).map(entry => entry.date);
}

// Ligne la plus probable pour le nom du produit : surtout des lettres, sans prix ni mention promo
export function guessTitle(lines: string[], storeName: string): string {
  const store = normalizeText(storeName);
  const candidates = lines
    .map(line => line.replace(/[|_*~=<>{}[\]\\]+/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(line => {
      const normalized = normalizeText(line);
      const letters = (normalized.match(/[a-z]/g) || []).length;
      return line.length >= 6 && line.length <= 90 && letters / line.length >= 0.6
        && line.split(' ').filter(word => word.length > 1).length >= 2
        && !NOT_TITLE.test(normalized) && (!store || normalized !== store);
    });
  const title = candidates[0] || '';
  // Étiquettes en majuscules : « LAIT DEMI-ÉCRÉMÉ » -> « Lait demi-écrémé »
  const lowered = title === title.toUpperCase() ? title.toLowerCase() : title;
  return lowered.charAt(0).toUpperCase() + lowered.slice(1);
}

// Prix du deal et prix habituel : « au lieu de », pourcentage cohérent, montant de la remise, sinon le plus petit et le plus grand
function pickPrices(lines: string[], percent: number | null): { priceDiscount: number | null; price: number | null } {
  let explicitOld: number | null = null;
  let savings: number | null = null;
  const candidates: number[] = [];
  for (const line of lines) {
    const normalized = normalizeText(line);
    const prices = pricesIn(line);
    if (!prices.length) {
      continue;
    }
    const insteadOf = normalized.indexOf('au lieu de');
    if (insteadOf >= 0) {
      const after = pricesIn(line.slice(insteadOf));
      explicitOld = after[0] ?? explicitOld;
      candidates.push(...pricesIn(line.slice(0, insteadOf)));
      continue;
    }
    if (UNIT_PRICE_LINE.test(normalized)) {
      continue;
    }
    if (SAVINGS_LINE.test(normalized)) {
      savings = savings ?? prices[0];
      continue;
    }
    candidates.push(...prices);
  }
  const unique = [...new Set(candidates)].sort((a, b) => a - b);
  const matchesPercent = (low: number, high: number) => percent !== null && Math.abs((1 - low / high) * 100 - percent) <= 2;

  if (explicitOld !== null) {
    const below = unique.filter(price => price < explicitOld);
    const priceDiscount = below.find(price => matchesPercent(price, explicitOld)) ?? (below.length ? below[below.length - 1] : null);
    return { priceDiscount, price: explicitOld };
  }
  if (percent !== null) {
    for (let i = 0; i < unique.length; i++) {
      for (let j = unique.length - 1; j > i; j--) {
        if (matchesPercent(unique[i], unique[j])) {
          return { priceDiscount: unique[i], price: unique[j] };
        }
      }
    }
  }
  if (savings !== null && unique.length) {
    const priceDiscount = unique[unique.length - 1];
    return { priceDiscount, price: round2(priceDiscount + savings) };
  }
  if (unique.length >= 2 && unique[0] / unique[unique.length - 1] >= 0.2) {
    return { priceDiscount: unique[0], price: unique[unique.length - 1] };
  }
  if (percent !== null && unique.length === 1 && percent > 0 && percent < 100) {
    // Un seul prix et un pourcentage : c'est le plus souvent le prix remisé
    return { priceDiscount: unique[0], price: round2(unique[0] / (1 - percent / 100)) };
  }
  return { priceDiscount: unique.length ? unique[unique.length - 1] : null, price: null };
}

export function parseInStoreText(text: string, today = new Date()): InStoreTextDetection {
  const lines = (text || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const normalized = normalizeText(text);
  const percentMatch = normalized.match(/-\s?(\d{1,2})\s?%/) || normalized.match(/(?<!\d)(\d{1,2})\s?%\s*(?:de\s+)?(?:remise|reduction|economie|offert|de moins)/);
  const percent = percentMatch ? Number(percentMatch[1]) : null;
  const storeName = detectStore(text);
  const { priceDiscount, price } = pickPrices(lines, percent);

  const todayIso = today.toISOString().slice(0, 10);
  const dates = datesIn(text, today);
  let startDate = '';
  let expirationDate = '';
  if (dates.length >= 2) {
    const sorted = [...dates].sort();
    startDate = sorted[0];
    expirationDate = sorted[sorted.length - 1];
  } else if (dates.length === 1) {
    expirationDate = dates[0];
  }
  // Date de fin déjà passée : sans doute une date de fabrication ou mal lue
  if (expirationDate && expirationDate < todayIso) {
    expirationDate = '';
    startDate = '';
  }

  return { title: guessTitle(lines, storeName), storeName, priceDiscount, price, percent, startDate, expirationDate };
}
